import { gql } from "@apollo/client";

export const UNREAD_CONVERSATION_COUNT = gql`
  query UNREAD_CONVERSATION_COUNT {
    # How many conversations have at least one unread inbound, non-system message
    conversations_aggregate(
      where: {
        archived: { _eq: false }
        messages: { read: { _eq: false }, isoutbound: { _eq: false }, is_system: { _eq: false } }
      }
    ) {
      aggregate {
        count
      }
    }

    # How many unread inbound, non-system messages exist (excluding archived conversations)
    messages_aggregate(
      where: {
        read: { _eq: false }
        isoutbound: { _eq: false }
        is_system: { _eq: false }
        conversation: { archived: { _eq: false } }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const CONVERSATION_TAB_UNREAD_COUNTS = gql`
  query CONVERSATION_TAB_UNREAD_COUNTS {
    active: messages_aggregate(
      where: {
        read: { _eq: false }
        isoutbound: { _eq: false }
        is_system: { _eq: false }
        conversation: { archived: { _eq: false } }
      }
    ) {
      aggregate {
        count
      }
    }
    archived: messages_aggregate(
      where: {
        read: { _eq: false }
        isoutbound: { _eq: false }
        is_system: { _eq: false }
        conversation: { archived: { _eq: true } }
      }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const CONVERSATION_LIST_QUERY = gql`
  query CONVERSATION_LIST_QUERY($offset: Int!, $archived: Boolean!) {
    conversations(order_by: { updated_at: desc }, limit: 50, offset: $offset, where: { archived: { _eq: $archived } }) {
      phone_num
      id
      updated_at
      unreadcnt
      archived
      label
      messages_aggregate(where: { read: { _eq: false }, isoutbound: { _eq: false }, is_system: { _eq: false } }) {
        aggregate {
          count
        }
      }
      job_conversations {
        jobid
        conversationid
        id
        job {
          id
          ro_number
          ownr_fn
          ownr_ln
          ownr_co_nm
        }
      }
    }
  }
`;

export const CONVERSATION_SUBSCRIPTION_BY_PK = gql`
  subscription CONVERSATION_SUBSCRIPTION_BY_PK($conversationId: uuid!) {
    messages(
      limit: 1
      order_by: [{ created_at: desc_nulls_last }, { id: desc }]
      where: { conversationid: { _eq: $conversationId } }
    ) {
      id
      conversationid
      status
      text
      is_system
      isoutbound
      image
      image_path
      userid
      created_at
      read
    }
  }
`;

export const GET_CONVERSATION_DETAILS = gql`
  query GET_CONVERSATION_DETAILS($conversationId: uuid!) {
    conversations_by_pk(id: $conversationId) {
      id
      phone_num
      archived
      updated_at
      unreadcnt
      label
      job_conversations {
        jobid
        conversationid
        id
        job {
          id
          ownr_fn
          ownr_ln
          ownr_co_nm
          ro_number
        }
      }
      messages(limit: 50, order_by: [{ created_at: desc_nulls_last }, { id: desc }]) {
        id
        conversationid
        status
        text
        is_system
        isoutbound
        image
        image_path
        userid
        created_at
        read
      }
    }
  }
`;

export const GET_OLDER_CONVERSATION_MESSAGES = gql`
  query GET_OLDER_CONVERSATION_MESSAGES(
    $conversationId: uuid!
    $beforeCreatedAt: timestamptz!
    $beforeId: uuid!
    $limit: Int!
  ) {
    messages(
      limit: $limit
      order_by: [{ created_at: desc_nulls_last }, { id: desc }]
      where: {
        conversationid: { _eq: $conversationId }
        _or: [
          { created_at: { _lt: $beforeCreatedAt } }
          { _and: [{ created_at: { _eq: $beforeCreatedAt } }, { id: { _lt: $beforeId } }] }
        ]
      }
    ) {
      id
      conversationid
      status
      text
      is_system
      isoutbound
      image
      image_path
      userid
      created_at
      read
    }
  }
`;

export const SEARCH_CONVERSATION_MESSAGES = gql`
  query SEARCH_CONVERSATION_MESSAGES($conversationId: uuid!, $search: String!, $limit: Int!, $offset: Int!) {
    matches: messages(
      limit: $limit
      offset: $offset
      order_by: [{ created_at: asc_nulls_last }, { id: asc }]
      where: { conversationid: { _eq: $conversationId }, is_system: { _eq: false }, text: { _ilike: $search } }
    ) {
      id
      created_at
    }
    matches_aggregate: messages_aggregate(
      where: { conversationid: { _eq: $conversationId }, is_system: { _eq: false }, text: { _ilike: $search } }
    ) {
      aggregate {
        count
      }
    }
  }
`;

export const GET_CONVERSATION_MESSAGE_CONTEXT = gql`
  query GET_CONVERSATION_MESSAGE_CONTEXT(
    $conversationId: uuid!
    $createdAt: timestamptz!
    $messageId: uuid!
    $limit: Int!
  ) {
    before: messages(
      limit: $limit
      order_by: [{ created_at: desc_nulls_last }, { id: desc }]
      where: {
        conversationid: { _eq: $conversationId }
        _or: [
          { created_at: { _lt: $createdAt } }
          { _and: [{ created_at: { _eq: $createdAt } }, { id: { _lte: $messageId } }] }
        ]
      }
    ) {
      id
      conversationid
      status
      text
      is_system
      isoutbound
      image
      image_path
      userid
      created_at
      read
    }
    after: messages(
      limit: $limit
      order_by: [{ created_at: asc_nulls_last }, { id: asc }]
      where: {
        conversationid: { _eq: $conversationId }
        _or: [
          { created_at: { _gt: $createdAt } }
          { _and: [{ created_at: { _eq: $createdAt } }, { id: { _gt: $messageId } }] }
        ]
      }
    ) {
      id
      conversationid
      status
      text
      is_system
      isoutbound
      image
      image_path
      userid
      created_at
      read
    }
  }
`;

export const CONVERSATION_ID_BY_PHONE = gql`
  query CONVERSATION_ID_BY_PHONE($phone: String!) {
    conversations(where: { phone_num: { _eq: $phone } }) {
      id
      phone_num
      archived
      label
      unreadcnt
      created_at
      job_conversations {
        id
        jobid
        conversationid
        job {
          id
          ownr_fn
          ownr_ln
          ownr_co_nm
          ro_number
        }
      }
      messages_aggregate(where: { read: { _eq: false }, isoutbound: { _eq: false }, is_system: { _eq: false } }) {
        aggregate {
          count
        }
      }
    }
  }
`;

export const CREATE_CONVERSATION = gql`
  mutation CREATE_CONVERSATION($conversation: [conversations_insert_input!]!) {
    insert_conversations(objects: $conversation) {
      returning {
        id
        phone_num
        archived
        label
        unreadcnt
        job_conversations {
          id
          jobid
          conversationid
          job {
            id
            ownr_fn
            ownr_ln
            ownr_co_nm
            ro_number
          }
        }
        messages_aggregate(where: { read: { _eq: false }, isoutbound: { _eq: false }, is_system: { _eq: false } }) {
          aggregate {
            count
          }
        }
      }
    }
  }
`;

export const TOGGLE_CONVERSATION_ARCHIVE = gql`
  mutation TOGGLE_CONVERSATION_ARCHIVE($id: uuid!, $archived: Boolean) {
    update_conversations_by_pk(pk_columns: { id: $id }, _set: { archived: $archived }) {
      archived
      id
    }
  }
`;

export const UPDATE_CONVERSATION_LABEL = gql`
  mutation UPDATE_CONVERSATION_LABEL($id: uuid!, $label: String) {
    update_conversations_by_pk(pk_columns: { id: $id }, _set: { label: $label }) {
      label
      id
    }
  }
`;
